<script setup lang="ts">
import { computed, onMounted, nextTick, useTemplateRef } from 'vue';
import type { FieldSchema } from '../../../types';
import { FILTER_MESSAGES, EMPTY_MESSAGES } from '@Constants';
import Dropdown from 'primevue/dropdown';

const emits = defineEmits<{
  change: [];
  onDropdownSelectValue: [];
}>();

const props = withDefaults(
  defineProps<{
    customClass?: string;
    disabled?: boolean;
    emptyMessage?: string;
    error?: string | boolean;
    field?: FieldSchema;
    hasFilter?: boolean;
    inputTextSize?: string;
    isInlineEditing?: boolean;
    options?: any[];
    optionLabel?: string;
    optionValue?: string;
    placeholder?: string;
    showClear?: boolean;
  }>(),
  {
    inputTextSize: 'text-sm',
    isInlineEditing: false,
    placeholder: 'components.forms.placeholder.select',
  }
);

const model = defineModel();

const inlineComponentRef = useTemplateRef<InstanceType<typeof Dropdown> | null>(
  'inlineComponentRef'
);

const settings = computed(() => props?.field?.settings);
const disabled = computed(() => props?.field?.state.disabled || props?.disabled);
const sanitizedOptions = computed(() => props?.field?.edit?.options || props?.options);
const filteredOptions = computed(() =>
  sanitizedOptions.value?.filter((opt: any) => opt !== null && opt !== undefined)
);

const options = computed(() => {
  const list =
    props?.field?.name === 'primary_color_map'
      ? props?.field?.edit?.options?.map((color: any) => color.value)
      : filteredOptions.value;

  if (!Array.isArray(list) || list.length === 0 || (list.length === 1 && list[0] == null))
    return [];

  return list;
});

onMounted(() => checkEditType());

function checkEditType() {
  if (!props.isInlineEditing || !inlineComponentRef.value) return;

  nextTick(() => {
    if (!inlineComponentRef.value) return;
    inlineComponentRef.value.show();
  });
}

function onSelectValue() {
  props.isInlineEditing ? emits('onDropdownSelectValue') : emits('change');
}
</script>

<template>
  <div>
    <p
      v-if="(!options || options.length === 0) && field?.label && !props.isInlineEditing"
      class="py-1 text-xs italic text-gray-500"
    >
      {{ $t('components.emptyMessage.dropdownEmpty', { field: field?.label }) }}
    </p>

    <Dropdown
      v-bind="{ FILTER_MESSAGES, EMPTY_MESSAGES }"
      v-model="model"
      pt:item="text-sm"
      pt:filterInput="h-9 py-1 pl-3 text-sm"
      pt:emptymessage="text-sm"
      pt:item-label="overflow-hidden text-ellipsis max-w-[290px] sm:max-w-[550px] xl:max-w-[620px]"
      ref="inlineComponentRef"
      :disabled
      :options
      :class="['relative flex h-9 w-full justify-between', customClass]"
      :emptyFilterMessage="$t('components.emptyFilterMessage.dropdown')"
      :emptyMessage="$t(emptyMessage || 'components.emptyMessage.dropdown')"
      :emptySelectionMessage="$t('components.emptySelectionMessage.dropdown')"
      :filter="hasFilter || (options ? options.length > 5 : undefined)"
      :filterMessage="$t('components.emptySelectionMessage.dropdown')"
      :filterResultMessage="$t('components.emptySelectionMessage.dropdown')"
      :invalid="!!error"
      :optionLabel="field?.edit?.label || optionLabel"
      :optionValue="field?.edit?.value || optionValue"
      :placeholder="$t(settings?.placeholder || placeholder)"
      :pt:trigger:class="{ 'text-red-600': !!error }"
      :pt:input:class="[
        inputTextSize,
        'flex items-center text-ellipsis overflow-hidden max-w-[290px] sm:max-w-[550px] xl:max-w-[620px]',
        { 'af-field-error': !!error },
      ]"
      :showClear="field?.settings?.showClear || showClear"
      @change="onSelectValue"
    />
  </div>
</template>
